import { useInfiniteQuery } from '@tanstack/react-query'
import { useCallback } from 'react'

export interface InfiniteScrollPage<TItem> {
  items: TItem[]
  totalItems: number
  totalPages: number
  page: number
}

interface UseInfiniteScrollQueryOptions<TData, TItem> {
  queryKey: unknown[]
  queryFn: (page: number, pageSize: number) => Promise<TData>
  transform: (data: TData, page: number) => InfiniteScrollPage<TItem>
  pageSize?: number
  enabled?: boolean
  staleTime?: number
  gcTime?: number
  refetchOnWindowFocus?: boolean
  refetchOnReconnect?: boolean
}

export interface UseInfiniteScrollQueryResult<TItem> {
  items: TItem[]
  totalItems: number
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: () => void
  error: Error | null
  refresh: () => void
}

/**
 * Generic infinite-scroll hook built on top of TanStack Query's useInfiniteQuery.
 *
 * Handles page accumulation, flattening, and exposes `totalItems` so callers can
 * show virtual placeholders for not-yet-loaded rows.
 */
export function useInfiniteScrollQuery<TData, TItem>({
  queryKey,
  queryFn,
  transform,
  pageSize = 10,
  enabled = true,
  staleTime = 2 * 60 * 1000,
  gcTime = 10 * 60 * 1000,
  refetchOnWindowFocus = false,
  refetchOnReconnect = true,
}: UseInfiniteScrollQueryOptions<TData, TItem>): UseInfiniteScrollQueryResult<TItem> {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 1 }) => {
      const raw = await queryFn(pageParam as number, pageSize)
      return transform(raw, pageParam as number)
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    enabled,
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnReconnect,
  })

  const items: TItem[] = data?.pages.flatMap((p) => p.items) ?? []
  const totalItems = data?.pages[0]?.totalItems ?? 0
  const refresh = useCallback(() => { refetch() }, [refetch])

  return {
    items,
    totalItems,
    isLoading,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    error: error as Error | null,
    refresh,
  }
}
